import { Btn } from '../actions';
import { IconSearch } from '../icons';
import { SearchSuggest, type SearchSuggestOption } from './SearchSuggest';

export interface SearchToolbarFilterOption {
    value: string;
    label: string;
}

export interface SearchToolbarProps {
    /** Options for the SearchSuggest combobox. */
    searchOptions: SearchSuggestOption[];
    /** Selected search value (option value, free-text query when `allowNewSearch`, or `''`). */
    searchValue: string;
    onSearchChange: (value: string) => void;
    searchPlaceholder?: string;
    searchAriaLabel?: string;
    searchInputId?: string;
    /**
     * When true, typed text can be committed as the search value (library-style free-text filter).
     * Defaults to true.
     */
    allowNewSearch?: boolean;
    filterOptions: SearchToolbarFilterOption[];
    filterValue: string;
    onFilterChange: (value: string) => void;
    filterAriaLabel?: string;
    className?: string;
    disabled?: boolean;
    /**
     * When false, omits the top/bottom border separators (e.g. modal toolbars).
     * Defaults to true.
     */
    bordered?: boolean;
}

/**
 * Horizontal search + filter-pill toolbar for library-style lists.
 * Uses IQUI `SearchSuggest` and `Btn` filter chips (see Daily Briefing Change Topic toolbar).
 */
export function SearchToolbar({
    searchOptions,
    searchValue,
    onSearchChange,
    searchPlaceholder = 'Search…',
    searchAriaLabel = 'Search',
    searchInputId,
    allowNewSearch = true,
    filterOptions,
    filterValue,
    onFilterChange,
    filterAriaLabel = 'Filter',
    className,
    disabled = false,
    bordered = true,
}: SearchToolbarProps) {
    const rootClassName = [
        'search-toolbar',
        bordered ? '' : 'search-toolbar--borderless',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={rootClassName}>
            <div className="search-toolbar-search">
                <IconSearch iconClasses="search-toolbar-search-icon" />
                <SearchSuggest
                    className="search-toolbar-suggest"
                    inputId={searchInputId}
                    ariaLabel={searchAriaLabel}
                    placeholder={searchPlaceholder}
                    options={searchOptions}
                    value={searchValue}
                    onChange={onSearchChange}
                    disabled={disabled}
                    allowNew={allowNewSearch}
                    clearable
                    emptyMessage="No matches found"
                    resolveValue={allowNewSearch ? (text) => text.trim() : undefined}
                />
            </div>
            <div className="search-toolbar-filters" role="group" aria-label={filterAriaLabel}>
                {filterOptions.map((option) => {
                    const isActive = filterValue === option.value;
                    return (
                        <Btn
                            key={option.value}
                            variant="secondary-outlined"
                            buttonSize="sm"
                            buttonText={option.label}
                            buttonOnClick={() => onFilterChange(option.value)}
                            buttonDisabled={disabled}
                            buttonClasses={`search-toolbar-filter${isActive ? ' is-active' : ''}`}
                            buttonAriaProps={{ 'aria-pressed': isActive }}
                        />
                    );
                })}
            </div>
        </div>
    );
}

export default SearchToolbar;
